import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Camera, 
  Video, 
  Upload, 
  CheckCircle2, 
  XCircle,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Home,
  Plus,
  Trash2,
  RotateCcw,
  Eye,
  Box,
  Scan
} from 'lucide-react';
import { toast } from 'sonner';
import Capture3D from '../components/Capture3D';

const API_URL = getApiBaseUrl();

const BaselineScan = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  // State
  const [session, setSession] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [currentRoomIndex, setCurrentRoomIndex] = useState(0);
  const [captures, setCaptures] = useState({});
  const [isCapturing, setIsCapturing] = useState(false);
  const [captureMode, setCaptureMode] = useState('photo'); // photo, video, 3d
  const [stream, setStream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // Refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  
  // Default room list
  const defaultRooms = [
    'Entrance/Hallway',
    'Living Room',
    'Kitchen',
    'Bedroom 1',
    'Bathroom 1',
  ];
  
  // Initialize
  useEffect(() => {
    if (token) {
      initializeSession();
    }
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [propertyId, token]);
  
  const initializeSession = async () => {
    try {
      setLoading(true);
      
      // Create baseline session
      const response = await fetch(`${API_URL}/api/scanning/baseline/sessions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          property_id: propertyId,
          required_rooms: defaultRooms,
          capture_mode: 'photo',
        }),
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to create session');
      }
      
      const sessionData = await response.json();
      setSession(sessionData);
      setRooms(sessionData.required_rooms || defaultRooms);
      
      // Initialize captures object
      const initialCaptures = {};
      (sessionData.required_rooms || defaultRooms).forEach(room => {
        initialCaptures[room] = [];
      });
      setCaptures(initialCaptures);
      
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Camera controls
  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setIsCapturing(true);
      
    } catch (err) {
      toast.error('Failed to access camera. Please ensure camera permissions are granted.');
      console.error('Camera error:', err);
    }
  };
  
  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setIsCapturing(false);
  };
  
  const capturePhoto = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    // Get image data
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    
    // Add to captures
    const currentRoom = rooms[currentRoomIndex];
    setCaptures(prev => ({
      ...prev,
      [currentRoom]: [...(prev[currentRoom] || []), {
        id: Date.now(),
        dataUrl,
        timestamp: new Date().toISOString(),
      }],
    }));
    
    toast.success('Photo captured!');
    
    // Upload to backend
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const formData = new FormData();
      formData.append('file', blob, `capture_${Date.now()}.jpg`);
      formData.append('room_name', currentRoom);
      formData.append('artefact_kind', 'raw_photo');
      
      await fetch(`${API_URL}/api/scanning/sessions/${session.id}/artefacts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });
    } catch (err) {
      console.error('Upload error:', err);
    }
  }, [rooms, currentRoomIndex, session, token]);
  
  const removeCapture = (roomName, captureId) => {
    setCaptures(prev => ({
      ...prev,
      [roomName]: prev[roomName].filter(c => c.id !== captureId),
    }));
  };
  
  // Room navigation
  const goToNextRoom = () => {
    if (currentRoomIndex < rooms.length - 1) {
      setCurrentRoomIndex(currentRoomIndex + 1);
    }
  };
  
  const goToPrevRoom = () => {
    if (currentRoomIndex > 0) {
      setCurrentRoomIndex(currentRoomIndex - 1);
    }
  };
  
  const addRoom = () => {
    const roomName = prompt('Enter room name:');
    if (roomName && !rooms.includes(roomName)) {
      setRooms([...rooms, roomName]);
      setCaptures(prev => ({ ...prev, [roomName]: [] }));
    }
  };
  
  // Progress calculation
  const getProgress = () => {
    const roomsWithCaptures = Object.entries(captures).filter(
      ([_, caps]) => caps.length >= 3
    ).length;
    return (roomsWithCaptures / rooms.length) * 100;
  };
  
  const isRoomComplete = (roomName) => {
    return (captures[roomName]?.length || 0) >= 3;
  };
  
  const canSubmit = () => {
    return rooms.every(room => isRoomComplete(room));
  };
  
  // Submit session
  const handleSubmit = async () => {
    if (!canSubmit()) {
      toast.error('Please capture at least 3 photos per room');
      return;
    }
    
    try {
      setSubmitting(true);
      
      const response = await fetch(`${API_URL}/api/scanning/sessions/${session.id}/submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Submission failed');
      }
      
      const result = await response.json();
      
      if (result.status === 'pass' || result.status === 'pass_with_warnings') {
        toast.success('Baseline scan completed successfully!');
        navigate(`/properties/${propertyId}`);
      } else {
        toast.warning(`Scan completed with status: ${result.status}`);
      }
      
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Initializing scan session...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button onClick={() => navigate(-1)} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }
  
  const currentRoom = rooms[currentRoomIndex];
  const currentCaptures = captures[currentRoom] || [];
  
  return (
    <div className="min-h-screen bg-slate-900 text-white" data-testid="baseline-scan-page">
      {/* Header */}
      <div className="bg-slate-800 border-b border-slate-700 px-4 py-3">
        <div className="flex items-center justify-between">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => {
              stopCamera();
              navigate(-1);
            }}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Exit
          </Button>
          <div className="text-center">
            <h1 className="font-semibold">Property Capture</h1>
            <p className="text-xs text-slate-400">Initial 3D Documentation</p>
          </div>
          <Badge variant={canSubmit() ? 'default' : 'secondary'}>
            {Math.round(getProgress())}%
          </Badge>
        </div>
        
        {/* Progress bar */}
        <Progress value={getProgress()} className="mt-3 h-1" />
      </div>
      
      {/* Room Navigation */}
      <div className="bg-slate-800/50 px-4 py-2 overflow-x-auto">
        <div className="flex gap-2 items-center">
          {rooms.map((room, index) => (
            <button
              key={room}
              onClick={() => setCurrentRoomIndex(index)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                index === currentRoomIndex
                  ? 'bg-indigo-600 text-white'
                  : isRoomComplete(room)
                  ? 'bg-green-600/20 text-green-400 border border-green-600/50'
                  : 'bg-slate-700 text-slate-300'
              }`}
            >
              {isRoomComplete(room) && <CheckCircle2 className="h-3 w-3 inline mr-1" />}
              {room}
            </button>
          ))}
          <button
            onClick={addRoom}
            className="flex-shrink-0 px-3 py-1.5 rounded-full text-sm bg-slate-700 text-slate-400 hover:bg-slate-600"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </div>
      
      {/* Current Room Info */}
      <div className="px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">{currentRoom}</h2>
            <p className="text-sm text-slate-400">
              {currentCaptures.length}/3 photos (minimum)
            </p>
          </div>
          <Badge variant={isRoomComplete(currentRoom) ? 'success' : 'secondary'}>
            {isRoomComplete(currentRoom) ? 'Complete' : 'In Progress'}
          </Badge>
        </div>
      </div>
      
      {/* Camera View / Captures Grid */}
      <div className="flex-1 px-4">
        {/* Mode selector */}
        {!isCapturing && (
          <div className="flex gap-2 mb-4">
            <Button
              size="sm"
              variant={captureMode === 'photo' ? 'default' : 'outline'}
              onClick={() => setCaptureMode('photo')}
              className={captureMode !== 'photo' ? 'border-slate-600 text-slate-400' : ''}
            >
              <Camera className="h-4 w-4 mr-1" />
              Photo
            </Button>
            <Button
              size="sm"
              variant={captureMode === '3d' ? 'default' : 'outline'}
              onClick={() => setCaptureMode('3d')}
              className={captureMode !== '3d' ? 'border-slate-600 text-slate-400' : ''}
            >
              <Box className="h-4 w-4 mr-1" />
              3D Capture
            </Button>
          </div>
        )}
        
        {/* 3D Capture Mode */}
        {captureMode === '3d' && !isCapturing ? (
          <Capture3D
            roomName={currentRoom}
            onCapture={(data) => {
              setCaptures(prev => ({
                ...prev,
                [currentRoom]: [...(prev[currentRoom] || []), {
                  id: Date.now(),
                  dataUrl: data.dataUrl,
                  timestamp: new Date().toISOString(),
                  mode: '3d',
                }],
              }));
            }}
            onComplete={async (result) => {
              // Upload 3D data to session
              if (session?.id) {
                try {
                  for (const capture of result.captures || []) {
                    const blob = await (await fetch(capture.dataUrl)).blob();
                    const formData = new FormData();
                    formData.append('file', blob, `3d_capture_${capture.id}.jpg`);
                    formData.append('room_name', currentRoom);
                    formData.append('artefact_kind', 'raw_photo');
                    formData.append('capture_mode', '3d_photogrammetry');
                    
                    await fetch(`${API_URL}/api/scanning/sessions/${session.id}/artefacts`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}` },
                      body: formData,
                    });
                  }
                  toast.success(`3D capture uploaded for ${currentRoom}`);
                } catch (err) {
                  console.error('Upload error:', err);
                }
              }
            }}
          />
        ) : isCapturing ? (
          <div className="relative rounded-xl overflow-hidden bg-black aspect-[4/3]">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
            
            {/* Capture overlay */}
            <div className="absolute inset-0 pointer-events-none">
              {/* Grid lines */}
              <div className="absolute inset-0 grid grid-cols-3 grid-rows-3">
                {[...Array(9)].map((_, i) => (
                  <div key={i} className="border border-white/20" />
                ))}
              </div>
            </div>
            
            {/* Capture button */}
            <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-4">
              <Button
                size="lg"
                variant="secondary"
                onClick={stopCamera}
              >
                <XCircle className="h-5 w-5" />
              </Button>
              <Button
                size="lg"
                className="w-16 h-16 rounded-full bg-white hover:bg-slate-200"
                onClick={capturePhoto}
              >
                <Camera className="h-8 w-8 text-slate-900" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Start capture button */}
            <button
              onClick={startCamera}
              className="w-full aspect-[4/3] rounded-xl bg-slate-800 border-2 border-dashed border-slate-600 flex flex-col items-center justify-center hover:border-indigo-500 hover:bg-slate-800/50 transition-all"
            >
              <Camera className="h-12 w-12 text-slate-400 mb-2" />
              <span className="text-slate-400">Tap to start camera</span>
            </button>
            
            {/* Captured photos grid */}
            {currentCaptures.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-slate-400 mb-2">
                  Captured Photos ({currentCaptures.length})
                </h3>
                <div className="grid grid-cols-3 gap-2">
                  {currentCaptures.map((capture) => (
                    <div key={capture.id} className="relative aspect-square rounded-lg overflow-hidden group">
                      <img
                        src={capture.dataUrl}
                        alt="Capture"
                        className="w-full h-full object-cover"
                      />
                      {capture.mode === '3d' && (
                        <div className="absolute top-1 left-1">
                          <Badge className="text-xs bg-indigo-600">3D</Badge>
                        </div>
                      )}
                      <button
                        onClick={() => removeCapture(currentRoom, capture.id)}
                        className="absolute top-1 right-1 p-1 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Hidden canvas for photo capture */}
      <canvas ref={canvasRef} className="hidden" />
      
      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-slate-800 border-t border-slate-700 px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <Button
            variant="outline"
            onClick={goToPrevRoom}
            disabled={currentRoomIndex === 0}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Previous
          </Button>
          
          {currentRoomIndex === rooms.length - 1 ? (
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit() || submitting}
              className="flex-1"
            >
              {submitting ? (
                <>Processing...</>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Complete Initial 3D
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={goToNextRoom}
              className="flex-1"
            >
              Next Room
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
        
        {/* Guidance */}
        <div className="mt-3 p-2 bg-slate-700/50 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-slate-400">
              Capture all corners, walls, windows, and key fixtures. 
              Minimum 3 photos per room for complete documentation.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BaselineScan;
